import { useFieldApi } from '@data-driven-forms/react-form-renderer';
import { FormElementWidgetWithOptions } from 'lib-node-amb-renderer';
import { useState } from 'react';

import FormControl from '~/common/componentMapper/FormControl';
import MultiSelectComponent from '~/common/mui/Inputs/Selects/MultiSelect/MultiSelect';

import { validationError } from '../utils';

// @ts-expect-error library defined options as private
export class MultiSelectWidget extends FormElementWidgetWithOptions {
  options;
  isMulti;
  constructor(props) {
    super(props);
    this.options = props.options;
    this.isMulti = props.isMulti;
  }
}

type MultiSelectProps = {
  options?: any[];
  isMulti?: string;
  label?: string;
  isLabelHidden?: boolean;
  validate?: any[];
  message?: string;
  onChange?: (...args: any[]) => any;
  onFocus?: (...args: any[]) => any;
  onBlur?: (...args: any[]) => any;
  name: string;
};

export default function MultiSelect(props: Readonly<MultiSelectProps>) {
  const {
    options,
    input,
    placeholder,
    fcStyle,
    isMulti,
    meta,
    validateOnMount,
    onChange,
    onBlur,
    onFocus,
  } = useFieldApi({
    ...props,
  });

  const isMultiSet = isMulti === 'true';

  const invalid = validationError(meta, validateOnMount);

  const selectName = props.name;
  const selectLabel = props.label;
  const isLabelHidden = props.isLabelHidden;
  const errorMessage =
    Array.isArray(props?.validate) && props?.validate[0]?.message;
  const [values, setValues] = useState(() => getValuesLabel(input.value));

  // helpers
  function isNotApplicable(value) {
    return [
      'IncomeId:0,BorrowerRecordId:0,BorrowerLenderDatabaseId:0',
      'AddressId:0,AddressLdId:0,IsAddressSubjectProperty:False',
      'AssetId:0,AssetLdId:0',
    ].includes(value);
  }

  function isNotListedAddToSystem(value) {
    return ['AssetId:-100,AssetLdId:-100'].includes(value);
  }

  function getValuesLabel(value) {
    let theLabelValue = [];

    if (value) {
      theLabelValue = options.reduce((acc, item) => {
        if (value.includes(item.value)) {
          acc.push({
            label: item.label,
            value: item.value,
          });
        }
        return acc;
      }, []);
    }
    return theLabelValue;
  }
  function sendValuesNoLabels(options) {
    let theValues = [];

    if (options) {
      theValues = options.reduce((acc, item) => {
        acc.push(item.value);
        return acc;
      }, []);
    }
    return theValues;
  }

  const handleChange = (_event, value) => {
    const selectedOptions = value;
    const currentSelection = selectedOptions[selectedOptions.length - 1];
    const selectDisplayArray = [];
    const selectSaveArray = [];

    if (!isMultiSet) {
      // handle change as a single select
      selectSaveArray.push(selectedOptions.value);
      selectDisplayArray.push(selectedOptions);
      setValues(selectDisplayArray);
      input.onChange(selectSaveArray);
      return;
    }

    // if NA selected, clear remaining selections
    if (isNotApplicable(currentSelection?.value)) {
      setValues([{ label: 'Not Applicable', value: currentSelection.value }]);
      input.onChange([currentSelection.value]);
    } else if (isNotListedAddToSystem(currentSelection?.value)) {
      setValues([
        {
          label: 'Not Listed - Add to system',
          value: currentSelection.value,
        },
      ]);
      input.onChange([currentSelection.value]);

      // if any option other than NA is selected, clear NA from list
    } else {
      const remaining = selectedOptions?.filter(
        (option) =>
          !isNotApplicable(option.value) &&
          !isNotListedAddToSystem(option.value),
      );

      const sV = remaining.length ? sendValuesNoLabels(remaining) : [];
      remaining.length ? setValues(remaining) : setValues([]);
      input.onChange(sV);
    }

    onChange?.(currentSelection);
  };

  return (
    <FormControl sx={{ ...fcStyle }} isInvalid={!!invalid}>
      <MultiSelectComponent
        multiple={isMultiSet}
        id={selectName}
        options={options}
        value={values}
        isLabelHidden={isLabelHidden}
        placeholder={placeholder}
        data-tracking={selectName}
        data-testid={selectName}
        label={selectLabel}
        onChange={(e, value) => {
          handleChange(e, value);
        }}
        onBlur={(e) => {
          input.onBlur(e);
          onBlur?.(e);
        }}
        onFocus={(e) => onFocus?.(e)}
        isOptionEqualToValue={(option, value) => {
          if (isMultiSet) {
            return option?.value === value?.value;
          } else {
            return (
              option?.value ===
              (Array.isArray(value) ? value[0]?.value : value?.value)
            );
          }
        }}
        errors={!!invalid}
        helperText={errorMessage}
        disableClearable
        filterSelectedOptions
      />
    </FormControl>
  );
}
